import React, { useCallback, useMemo } from "react";
import clsx from "clsx";
import { format } from "date-fns";

import { Icon } from "@proda-ai/ui-kit";
import { IconBox } from "@proda-ai/ui-kit";

import Box from "@/ui/atoms/Box";
import Badge from "@/ui/atoms/Badge";
import Typography from "@/ui/atoms/Typography";

import type {
    GridProps,
    GetRowIdFunc,
    ColDef,
    ICellRendererParams,
    IsGroupOpenByDefaultParams,
} from "@/ui/molecules/Grid";
import Grid, { GridLoaderSkeleton } from "@/ui/molecules/Grid";
import AccountWithType from "@/ui/molecules/AccountWithType";
import type { EmptyGridMessageRendererProps } from "@/ui/molecules/EmptyGridMessageRenderer";
import EmptyGridMessageRenderer from "@/ui/molecules/EmptyGridMessageRenderer";

import { isNullable } from "@/utils/guards";

const INTERNAL_ERROR_MESSAGE =
    "There was an error fetching audit logs. Please try refreshing the page.";
const GRID_EMPTY_MESSAGE = "No audit logs found.";
const GRID_EMPTY_SUBMESSAGE = "Sorry, we couldn't find any matching audit logs";

type TAccountType = "income" | "expense" | "non-operating";

export type TAuditLogEntry = {
    id: number;
    accountCode: string;
    accountName?: string | null;
    accountType?: TAccountType | null;
    userId: number;
    modifiedAt: string;
    changesGroupId: string;
    userEmail?: string | null;
    fromAccountId?: number | null;
    fromAccountCode?: string | null;
    fromAccountName?: string | null;
    fromAccountType?: TAccountType | null;
    toAccountId?: number | null;
    toAccountCode?: string | null;
    toAccountName?: string | null;
    toAccountType?: TAccountType | null;
};

export type GridChartOfAccountsAuditLogProps = Omit<
    GridProps<TAuditLogEntry>,
    "columnDefs" | "rowData" | "getRowStyle"
> & {
    rowData: TAuditLogEntry[];
    currentUserId: number;
};

export const testIds = {
    main: "GridChartOfAccountsAuditLog",
    header: "GridChartOfAccountsAuditLog-header",
    titleBar: "GridChartOfAccountsAuditLog-titleBar",
};

const formatDate = (dateStr: string | null | undefined): string => {
    if (dateStr === null || dateStr === undefined) {
        return "-";
    }

    try {
        return format(new Date(dateStr), "yyyy-MM-dd");
    } catch {
        return String(dateStr);
    }
};

const formatTime = (dateStr: string | null | undefined): string => {
    if (dateStr === null || dateStr === undefined) {
        return "-";
    }

    try {
        return format(new Date(dateStr), "HH:mm:ss");
    } catch {
        return String(dateStr);
    }
};

const CustomGroupCellRenderer = (params: {
    node: {
        group: boolean;
        key: string;
        expanded: boolean;
        setExpanded: (expanded: boolean) => void;
        childrenAfterSort?: Array<{
            data?: TAuditLogEntry;
        }>;
        allChildrenCount?: number;
    };
}) => {
    if (params.node.group !== true) {
        return null;
    }

    let modifiedAt: string | null = null;
    if (
        params.node.childrenAfterSort &&
        params.node.childrenAfterSort.length > 0
    ) {
        const firstChild = params.node.childrenAfterSort[0];
        if (firstChild && firstChild.data) {
            modifiedAt = firstChild.data.modifiedAt;
        }
    }

    const formattedDate = formatDate(modifiedAt);
    const formattedTime = formatTime(modifiedAt);
    const changeCount =
        typeof params.node.allChildrenCount === "number"
            ? params.node.allChildrenCount
            : 0;

    const expanded = params.node.expanded;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "100%",
                height: "100%",
                paddingLeft: "16px",
                paddingRight: "16px",
                cursor: "pointer",
                backgroundColor: "var(--proda-white-120)",
                gap: "16px",
            }}
            onClick={() => params.node.setExpanded(!expanded)}
        >
            <Box>
                <IconBox>
                    <Icon icon={expanded ? "chevron-down" : "chevron-right"} />
                </IconBox>
            </Box>

            <Badge severity="info" leadingIcon={null}>
                {changeCount} change
                {changeCount === 1 ? "" : "s"}
            </Badge>

            <Box>
                <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", marginRight: "4px" }}
                >
                    {formattedDate}
                </Typography>
                <Typography variant="body2">{formattedTime}</Typography>
            </Box>
        </Box>
    );
};

const PRODAAccountCellRenderer = (
    params: ICellRendererParams<TAuditLogEntry>,
) => {
    if (!params.data) {
        return null;
    }

    const type = params.data.accountType;
    const name = params.data.accountName;

    if (isNullable(type) || isNullable(name)) {
        return null;
    }

    return <AccountWithType type={type} name={name} />;
};

const FromAccountCellRenderer = (
    params: ICellRendererParams<TAuditLogEntry>,
) => {
    if (!params.data) {
        return null;
    }

    const code = params.data.fromAccountCode;
    const type = params.data.fromAccountType;
    const name = params.data.fromAccountName;

    if (isNullable(code)) {
        return <AccountWithType type={null} name={"Unmapped"} />;
    }

    if (isNullable(type) || isNullable(name)) {
        return null;
    }

    return <AccountWithType type={type} name={name} />;
};

const ToAccountCellRenderer = (params: ICellRendererParams<TAuditLogEntry>) => {
    if (!params.data) {
        return null;
    }

    const code = params.data.toAccountCode;
    const type = params.data.toAccountType;
    const name = params.data.toAccountName;

    if (isNullable(code)) {
        return <AccountWithType type={null} name={"Unmapped"} />;
    }

    if (isNullable(type) || isNullable(name)) {
        return null;
    }

    return <AccountWithType type={type} name={name} />;
};

const GridChartOfAccountsAuditLog = React.memo(
    (props: GridChartOfAccountsAuditLogProps) => {
        const { rowData, className, currentUserId, ...otherProps } = props;

        const getRowId: GetRowIdFunc<TAuditLogEntry> = useCallback(
            params => String(params.data.id),
            [],
        );

        const UserEmailCellRenderer = React.useCallback(
            (params: ICellRendererParams<TAuditLogEntry>) => {
                if (!params.data) {
                    return null;
                }

                const userId = params.data.userId;
                const userEmail = params.data.userEmail;

                if (userId === currentUserId) {
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                width: "100%",
                                height: "100%",
                            }}
                        >
                            <Badge severity="success" leadingIcon={null}>
                                You
                            </Badge>
                            <Typography variant="body2">{userEmail}</Typography>
                        </Box>
                    );
                }

                return <Typography variant="body2">{userEmail}</Typography>;
            },
            [currentUserId],
        );

        const columnDefs = useMemo<ColDef<TAuditLogEntry>[]>(
            () => [
                {
                    field: "changesGroupId",
                    headerName: "Change Group",
                    enableRowGroup: true,
                    rowGroup: true,
                    hide: true,
                },
                {
                    field: "userEmail",
                    headerName: "Modified By",
                    cellRenderer: UserEmailCellRenderer,
                },
                {
                    headerName: "PRODA Chart of Accounts",
                    cellRenderer: PRODAAccountCellRenderer,
                },
                {
                    headerName: "Custom mappings",
                    children: [
                        {
                            headerName: "From",
                            cellRenderer: FromAccountCellRenderer,
                        },
                        {
                            headerName: "To",
                            cellRenderer: ToAccountCellRenderer,
                        },
                    ],
                },
            ],
            [UserEmailCellRenderer],
        );

        const defaultColDef = useMemo(
            () => ({
                flex: 1,
                minWidth: 120,
                sortable: false,
                filter: false,
                resizable: false,
            }),
            [],
        );

        const rowDataSortedByDate = useMemo(() => {
            return rowData.toSorted((a, b) => {
                return (
                    new Date(b.modifiedAt).getTime() -
                    new Date(a.modifiedAt).getTime()
                );
            });
        }, [rowData]);
        const latestGroupKey = useMemo(() => {
            const latestRowData = rowDataSortedByDate[0];

            if (!latestRowData) {
                return null;
            }

            return latestRowData.changesGroupId;
        }, [rowDataSortedByDate]);
        const isLatestGroup = useCallback(
            (params: IsGroupOpenByDefaultParams<TAuditLogEntry>) => {
                const rowNode = params.rowNode;
                return rowNode.key === latestGroupKey;
            },
            [latestGroupKey],
        );

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "100%",
                    flexGrow: 1,
                    overflow: "auto",
                }}
            >
                <Grid<TAuditLogEntry>
                    data-testid={testIds.main}
                    className={clsx([testIds.main, className])}
                    gridTheme="ag-theme-custom-proda"
                    readOnlyEdit={true}
                    rowData={rowDataSortedByDate}
                    columnDefs={columnDefs}
                    getRowId={getRowId}
                    defaultColDef={defaultColDef}
                    noRowsOverlayComponent={EmptyGridMessageRenderer}
                    noRowsOverlayComponentParams={
                        {
                            errorMessage: INTERNAL_ERROR_MESSAGE,
                            emptyMessage: GRID_EMPTY_MESSAGE,
                            emptySubMessage: GRID_EMPTY_SUBMESSAGE,
                        } as EmptyGridMessageRendererProps
                    }
                    getRowStyle={() => ({
                        backgroundColor: "var(--proda-white)",
                    })}
                    suppressHeaderFocus={true}
                    enableBrowserTooltips={true}
                    headerHeight={48}
                    groupHeaderHeight={48}
                    gridLoaderComponent={GridLoaderSkeleton}
                    groupDisplayType="groupRows"
                    groupRowRenderer={CustomGroupCellRenderer}
                    autoSizeStrategy={{ type: "fitGridWidth" }}
                    isGroupOpenByDefault={isLatestGroup}
                    {...otherProps}
                />
            </Box>
        );
    },
);

GridChartOfAccountsAuditLog.displayName = "GridChartOfAccountsAuditLog";

export default GridChartOfAccountsAuditLog;
